import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { TokenJWT } from 'src/app/core/models/token/token-jwt';
import { TokenService } from 'src/app/core/services/token/token.service';
import { environment } from 'src/environments/environment';
import { ChatVeterinariaAutoPromotion, ChatVeterinariaProductResponse } from '../../models/chat-veterinaria-product';

@Injectable({
  providedIn: 'root'
})
export class ChatVeterinariaDataService implements OnDestroy {
  private token: TokenJWT | undefined;
  private tokenSubscription$: Subscription = new Subscription();

  constructor(private tokenService: TokenService) {
    this.getToken();
  }

  ngOnDestroy(): void {
    if (this.tokenSubscription$) this.tokenSubscription$.unsubscribe();
  }

  private getToken(): void {
    this.tokenSubscription$ = this.tokenService
      .getToken()
      .subscribe((token) => {
        this.token = token;
      });
  }

  private waitForToken(timeoutMs = 3000): Promise<void> {
    return new Promise((resolve, reject) => {
      const interval = 50;
      let waited = 0;

      const check = () => {
        if (this.token) {
          resolve();
        } else if (waited >= timeoutMs) {
          reject(new Error('Token non disponibile entro il tempo limite'));
        } else {
          waited += interval;
          setTimeout(check, interval);
        }
      };

      check();
    });
  }

  /**
   * Legge l'abbonamento Chat Veterinaria attivo dell'account.
   * Restituisce l'oggetto abbonamento se presente (IDStato = 1 = attivo), null altrimenti.
   */
  public async readSubscription(): Promise<any | null> {
    try {
      await this.waitForToken();

      const response = await fetch(
        environment.apiUrl +
        environment.apiVersion +
        '/chat-veterinaria/subscription/read',
        {
          headers: { Authorization: this.token!.toString() },
        }
      );

      const data = await response.json();

      if (!response.ok) return null;

      return data.body ?? null;

    } catch (error) {
      console.error(error);
      return null;
    }
  }

  /**
   * Crea la sottoscrizione Chat Veterinaria e restituisce il clientSecret per la conferma Stripe.
   * @param priceId ID del piano selezionato
   * @param paymentMethodId ID del metodo di pagamento scelto
   */
  public async pay(priceId: string, paymentMethodId: string, promotionId?: string, couponCode?: string): Promise<{ clientSecret: string | null } | null> {
    try {
      await this.waitForToken();

      const body: any = { priceId, paymentMethodId };
      if (promotionId) body.promotionId = promotionId;
      if (couponCode) body.codice = couponCode;

      const response = await fetch(
        environment.apiUrl +
        environment.apiVersion +
        '/chat-veterinaria/subscription/pay/create',
        {
          headers: {
            Authorization: this.token!.toString(),
            'Content-Type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify(body),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return { clientSecret: data.body.clientSecret ?? null };

    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Crea una sessione del Customer Portal Stripe per la gestione autonoma dell'abbonamento Chat Veterinaria.
   * @param returnUrl URL a cui tornare dopo aver chiuso il portale
   * @returns URL del portale Stripe, o null in caso di errore
   */
  public async readPortalUrl(returnUrl: string): Promise<string | null> {
    try {
      await this.waitForToken();

      const response = await fetch(
        environment.apiUrl +
        environment.apiVersion +
        '/chat-veterinaria/subscription/portal/create',
        {
          headers: {
            Authorization: this.token!.toString(),
            'Content-Type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify({ returnUrl }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return data.body.url ?? null;

    } catch (error) {
      console.error(error);
      return null;
    }
  }

  /**
   * Collega un abbonamento Chat Veterinaria acquistato da web all'account dell'utente.
   * @param codice Codice ricevuto via email dopo l'acquisto sul sito
   * @returns true se collegato, 'not_found' se codice non valido, false per altri errori
   */
  public async linkSubscription(codice: string): Promise<true | 'not_found' | false> {
    try {
      await this.waitForToken();

      const response = await fetch(
        environment.apiUrl +
        environment.apiVersion +
        '/chat-veterinaria/subscription/activation-code/validate',
        {
          method: 'POST',
          headers: {
            Authorization: this.token!.toString(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ codice: codice }),
        }
      );

      if (response.status === 404) return 'not_found';
      if (!response.ok) return false;

      return true;

    } catch (error) {
      console.error(error);
      return false;
    }
  }

  /**
   * Richiede al server le promozioni/coupon applicabili al prodotto Chat Veterinaria
   */
  public async readPromotions(): Promise<ChatVeterinariaAutoPromotion | null> {
    try {
      await this.waitForToken();

      const response = await fetch(
        environment.apiUrl +
        environment.apiVersion +
        '/chat-veterinaria/promotions/read',
        {
          headers: { Authorization: this.token!.toString() },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return data.body ?? null;

    } catch (error) {
      console.error(error);
      return null;
    }
  }

  /**
   * Verifica un codice sconto inserito manualmente.
   * @returns L'oggetto sconto se valido, null se non trovato, 'unprocessable' se non applicabile al servizio Chat Veterinaria
   */
  public async verifyCoupon(codice: string): Promise<{ percentOff: number | null; amountOff: number | null; currency: string | null; duration: string } | null | 'unprocessable'> {
    try {
      await this.waitForToken();

      const response = await fetch(
        environment.apiUrl +
        environment.apiVersion +
        '/chat-veterinaria/promotions/coupon/verify',
        {
          method: 'POST',
          headers: {
            Authorization: this.token!.toString(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ codice }),
        }
      );

      if (response.status === 404) return null;
      if (response.status === 422) return 'unprocessable';

      const data = await response.json();

      if (!response.ok) throw new Error(`${data.description}`);

      return data.body;

    } catch (error) {
      console.error(error);
      return null;
    }
  }

  /**
   * Richiede al server i prodotti Chat Veterinaria disponibili
   */
  public async readProducts(): Promise<ChatVeterinariaProductResponse | null> {
    try {
      await this.waitForToken();

      const response = await fetch(
        environment.apiUrl +
        environment.apiVersion +
        '/chat-veterinaria/products/read',
        {
          headers: { Authorization: this.token!.toString() },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return data.body;

    } catch (error) {
      console.error(error);
      return null;
    }
  }
}
